import React, { useState, useCallback, useEffect } from 'react';
import { alarmsAPI } from '../services/api';
import { usePolling } from '../hooks/usePolling';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const LEVEL_COLOR = { crit: '#e63946', warn: '#f5a623', info: '#f5a623', ok: '#22c55e' };
const LEVEL_BG    = { crit: 'rgba(248,81,73,.1)', warn: 'rgba(227,179,65,.1)', info: 'rgba(88,166,255,.08)', ok: 'rgba(63,185,80,.08)' };

function AlarmRow({ alarm, onAck, onResolve }) {
  const level = alarm.level || 'info';
  const color = LEVEL_COLOR[level] || LEVEL_COLOR.info;
  const bg    = LEVEL_BG[level] || LEVEL_BG.info;
  return (
    <div style={{ ...S.row, background: alarm.acked ? 'transparent' : bg }}>
      <div style={{ ...S.dot, background: color, boxShadow: alarm.acked ? 'none' : `0 0 8px ${color}` }} />
      <div style={{ flex: 1 }}>
        <div style={{ ...S.rowName, color: alarm.acked ? 'rgba(255,255,255,.72)' : '#ffffff' }}>{alarm.name}</div>
        <div style={S.rowMeta}>{alarm.server} · {alarm.time}</div>
      </div>
      <span style={{ ...S.tag, background: `${color}22`, color }}>
        {alarm.level.toUpperCase()}
      </span>
      {alarm.type === 'sos' && !alarm.acked && (
        <button style={S.sosBtn} onClick={() => onResolve(alarm.id)}>Résoudre SOS</button>
      )}
      {!alarm.acked && (
        <button style={S.ackBtn} onClick={() => onAck(alarm.id)}>ACK</button>
      )}
      {alarm.acked && <span style={S.resolved}>✓</span>}
    </div>
  );
}

export default function AlarmsPage() {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAlarms = useCallback(async () => {
    const [active, history] = await Promise.allSettled([
      alarmsAPI.getActive(),
      alarmsAPI.getHistory(20),
    ]);

    if (active.status === 'rejected' && history.status === 'rejected') {
      throw new Error('alarms_unavailable');
    }

    const activeData = active.status === 'fulfilled' ? (active.value.data || []) : [];
    const historyData = history.status === 'fulfilled' ? (history.value.data || []) : [];
    const partialError = active.status === 'rejected'
      ? 'Alarmes actives indisponibles temporairement.'
      : history.status === 'rejected'
        ? 'Historique des alarmes indisponible temporairement.'
        : '';

    return { data: [...activeData, ...historyData], partialError };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const result = await fetchAlarms();
        if (mounted) {
          setAlarms(result.data);
          setError(result.partialError);
        }
      } catch (err) {
        if (mounted) {
          setAlarms([]);
          setError('Impossible de charger les alarmes reelles. Verifiez le backend monitor.');
          toast.error('Alarmes reelles indisponibles');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [fetchAlarms]);

  const pollAlarms = useCallback(async () => {
    try {
      const result = await fetchAlarms();
      setAlarms(result.data);
      setError(result.partialError);
    } catch {
      setError('Impossible de rafraichir les alarmes reelles. Derniere lecture conservee.');
    }
  }, [fetchAlarms]);

  usePolling(pollAlarms, 15000);

  const ack = async (id) => {
    try {
      await alarmsAPI.acknowledge(id);
    } catch { /* offline */ }
    setAlarms(prev => prev.map(a => a.id === id ? { ...a, acked: true } : a));
    toast.success('Alarme acquittée');
  };

  const ackAll = async () => {
    try { await alarmsAPI.acknowledgeAll(); } catch {}
    setAlarms(prev => prev.map(a => ({ ...a, acked: true })));
    toast.success('Toutes les alarmes acquittées');
  };

  const resolveSOSAlarm = async (id) => {
    try { await alarmsAPI.resolveSOS(id, 'Résolu depuis le dashboard'); } catch {}
    setAlarms(prev => prev.map(a => a.id === id ? { ...a, acked: true } : a));
    toast.success('SOS résolu');
  };

  const active   = alarms.filter(a => !a.acked);
  const resolved = alarms.filter(a => a.acked);
  const crit     = active.filter(a => a.level === 'crit').length;
  const warn     = active.filter(a => a.level === 'warn').length;
  const sos      = active.filter(a => a.type === 'sos').length;

  return (
    <div style={S.page}>
      {/* Summary */}
      <div style={S.summary}>
        <div style={S.summaryCard}>
          <div style={S.sLabel}>CRITIQUES</div>
          <div style={{ ...S.sVal, color: '#e63946' }}>{crit}</div>
        </div>
        <div style={S.summaryCard}>
          <div style={S.sLabel}>WARNINGS</div>
          <div style={{ ...S.sVal, color: '#f5a623' }}>{warn}</div>
        </div>
        <div style={S.summaryCard}>
          <div style={S.sLabel}>SOS ACTIFS</div>
          <div style={{ ...S.sVal, color: crit > 0 ? '#e63946' : '#22c55e' }}>{sos}</div>
        </div>
        <div style={S.summaryCard}>
          <div style={S.sLabel}>RÉSOLUES (session)</div>
          <div style={{ ...S.sVal, color: '#22c55e' }}>{resolved.length}</div>
        </div>
      </div>

      {/* Active alarms */}
      <div style={S.card}>
        <div style={S.cardHeader}>
          <span style={S.cardTitle}>ALARMES ACTIVES</span>
          {active.length > 0 && (
            <button style={S.allAckBtn} onClick={ackAll}>✓ Tout acquitter</button>
          )}
        </div>
        {error && <div style={S.error}>{error}</div>}
        {loading && <div style={S.empty}>Chargement des alarmes reelles...</div>}
        {!loading && active.length === 0 ? (
          <div style={S.empty}>✓ Aucune alarme active — systèmes nominaux</div>
        ) : !loading && (
          active.map(a => (
            <AlarmRow key={a.id} alarm={a} onAck={ack} onResolve={resolveSOSAlarm} />
          ))
        )}
      </div>

      {/* History */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.cardHeader}>
          <span style={S.cardTitle}>HISTORIQUE</span>
        </div>
        {resolved.map(a => (
          <AlarmRow key={a.id} alarm={a} onAck={ack} onResolve={resolveSOSAlarm} />
        ))}
      </div>
    </div>
  );
}

const S = {
  page:     { padding: 20 },
  summary:  { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 },
  summaryCard: { background: '#10224a', border: '1px solid rgba(255,255,255,.14)', borderRadius: 6, padding: '14px 16px' },
  sLabel:   { fontSize: 10, color: 'rgba(255,255,255,.58)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 },
  sVal:     { fontSize: 26, fontWeight: 700, letterSpacing: -1 },
  card:     { background: '#10224a', border: '1px solid rgba(255,255,255,.14)', borderRadius: 6, overflow: 'hidden' },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.14)' },
  cardTitle:  { fontSize: 11, fontWeight: 700, color: '#ffffff', letterSpacing: .5 },
  allAckBtn:  { background: 'rgba(248,81,73,.1)', border: '1px solid rgba(248,81,73,.3)', color: '#e63946', fontFamily: 'monospace', fontSize: 11, padding: '4px 12px', borderRadius: 4, cursor: 'pointer' },
  row:      { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: '1px solid rgba(48,54,61,.5)', transition: 'background .2s' },
  dot:      { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  rowName:  { fontSize: 12, marginBottom: 2 },
  rowMeta:  { fontSize: 10, color: 'rgba(255,255,255,.58)' },
  tag:      { fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 700, whiteSpace: 'nowrap' },
  ackBtn:   { background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.14)', color: 'rgba(255,255,255,.72)', fontFamily: 'monospace', fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer' },
  sosBtn:   { background: 'rgba(248,81,73,.15)', border: '1px solid rgba(248,81,73,.4)', color: '#e63946', fontFamily: 'monospace', fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer' },
  resolved: { color: '#22c55e', fontSize: 14, marginLeft: 4 },
  empty:    { padding: 20, color: '#22c55e', fontSize: 12, textAlign: 'center' },
  error:    { margin: 14, padding: 12, border: '1px solid rgba(248,81,73,.35)', background: 'rgba(248,81,73,.1)', color: '#fda29b', borderRadius: 6, fontSize: 12, textAlign: 'center' },
};
